"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { StepNavigation } from "@/components/backoffice/step-navigation";
import { VehicleAssignmentStepProgress } from "@/components/backoffice/cat/vehicle-assignment/vehicle-assignment-step-progress";
import { VehicleAssignmentAvsSteps } from "@/components/backoffice/cat/vehicle-assignment/avs/vehicle-assignment-avs-steps";
import type { SalesTypeValue } from "@/components/backoffice/cat/vehicle-assignment/sales-channels-step";
import { useStep } from "@/hooks/use-step-handler";
import { useVehicleAssignmentFlowCleanup } from "@/hooks/use-vehicle-assignment-flow-cleanup";
import { getAvsVehicleAssignmentVisibleDefinitions } from "@/lib/cat/vehicle-assignment-avs-step-definitions";
import {
  toVehicleAssignmentProgressSteps,
  type VehicleAssignmentFlowContext,
} from "@/lib/cat/vehicle-assignment-step-definitions";
import type { Kvps } from "@/lib/kvps";

function VehicleAssignmentAvsFlow({
  currentKvps,
}: {
  currentKvps?: Kvps | null;
}) {
  const { handleStepChange, step } = useStep();
  useVehicleAssignmentFlowCleanup();
  const [canProceed, setCanProceed] = useState(true);
  const [avsSalesChannel, setAvsSalesChannel] = useState<SalesTypeValue | "">(
    "",
  );
  const [avsInternalAccountId, setAvsInternalAccountId] = useState("");
  const beforeNextHandlerRef = useRef<
    (() => boolean | void | Promise<boolean | void>) | null
  >(null);

  const registerBeforeNextHandler = useCallback(
    (handler: (() => boolean | void | Promise<boolean | void>) | null) => {
      beforeNextHandlerRef.current = handler;
    },
    [],
  );

  const handleAvsSalesChannelChange = useCallback((value: SalesTypeValue) => {
    setAvsSalesChannel(value);
  }, []);

  const handleAvsInternalAccountIdChange = useCallback((value: string) => {
    setAvsInternalAccountId(value);
  }, []);

  const ctx: VehicleAssignmentFlowContext = useMemo(
    () => ({
      avsInternalAccountId,
      avsSalesChannel,
      kvps: currentKvps ?? null,
    }),
    [avsInternalAccountId, avsSalesChannel, currentKvps],
  );

  const visibleDefinitions = useMemo(
    () => getAvsVehicleAssignmentVisibleDefinitions(ctx),
    [ctx],
  );

  const stepsAvs = useMemo(
    () => toVehicleAssignmentProgressSteps(visibleDefinitions),
    [visibleDefinitions],
  );

  const maxStep = stepsAvs.length;
  const minStep = 1;

  const runBeforeNextHandler = useCallback(async (): Promise<boolean> => {
    const handler = beforeNextHandlerRef.current;
    if (!handler) {
      return true;
    }

    try {
      const result = await handler();
      return result !== false;
    } catch {
      return false;
    }
  }, []);

  const handleNext = useCallback(async () => {
    const canAdvance = await runBeforeNextHandler();
    if (!canAdvance) {
      return;
    }
    handleStepChange(Math.min(step + 1, maxStep));
  }, [handleStepChange, maxStep, runBeforeNextHandler, step]);

  useEffect(() => {
    if (step > maxStep) {
      handleStepChange(maxStep);
    }
  }, [handleStepChange, maxStep, step]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-4 gap-8">
        <div className="col-span-1">
          <VehicleAssignmentStepProgress currentStep={step} steps={stepsAvs} />
        </div>

        <div className="col-span-3">
          <VehicleAssignmentAvsSteps
            avsInternalAccountId={avsInternalAccountId}
            avsSalesChannel={avsSalesChannel}
            currentKvps={currentKvps}
            onAvsInternalAccountIdChange={handleAvsInternalAccountIdChange}
            onAvsSalesChannelChange={handleAvsSalesChannelChange}
            onBeforeNextChange={registerBeforeNextHandler}
            onCanProceedChange={setCanProceed}
          />
          <StepNavigation
            isNextButtonDisabled={!canProceed}
            onNext={() => void handleNext()}
            onPrevious={() => handleStepChange(Math.max(step - 1, minStep))}
            showNextButton={step < maxStep}
            showPreviousButton={step > minStep}
          />
        </div>
      </div>
    </div>
  );
}

export { VehicleAssignmentAvsFlow };
